import API, { baseURL } from './api';
import { getSucursalInfo } from './auth';

// Client for the debt/payment report endpoints in sge_infantil_core's
// routes/report.js and routes/payment.js — both PDF-generating report screens.
export async function getDebtReportPdf({ year, months, type, courseId, sucursalId }) {
  const id = sucursalId || getSucursalInfo()?.id || 1;
  const params = { year };
  if (months?.length) params.months = months.join(",");
  if (type) params.type = type;
  if (courseId) params.courseId = courseId;
  return API.get(`${baseURL}api/report/count-debt-multi/${id}`, { params });
}

// "Pagamentos Efectuados" — mirror of getDebtReportPdf above, but for the
// sge_infantil_core GET /api/report/count-payments-made-multi/:sucursalId
// endpoint: same year/months/type/courseId filters, plus extraIds/taxIds to
// narrow type='extra'/'tax' down to specific extras or taxas ("Extras
// Específicos" / "Taxas Específicas") instead of every one of them.
function buildPaymentsMadeParams({ year, months, type, courseId, extraIds, taxIds, allMonthlyPaid }) {
  const params = { year };
  if (months?.length) params.months = months.join(",");
  if (type) params.type = type;
  if (courseId) params.courseId = courseId;
  if (extraIds?.length) params.extraIds = extraIds.join(",");
  if (taxIds?.length) params.taxIds = taxIds.join(",");
  if (allMonthlyPaid) params.allMonthlyPaid = "true";
  return params;
}

export async function getPaymentsMadeReportPdf({ year, months, type, courseId, extraIds, taxIds, allMonthlyPaid, sucursalId }) {
  const id = sucursalId || getSucursalInfo()?.id || 1;
  const params = buildPaymentsMadeParams({ year, months, type, courseId, extraIds, taxIds, allMonthlyPaid });
  return API.get(`${baseURL}api/report/count-payments-made-multi/${id}`, { params });
}

// JSON counterpart of getPaymentsMadeReportPdf — sge_infantil_core's GET
// /api/report/payments-made-multi-list/:sucursalId, same filters, grouped by
// student, but returned as data for the "Consultar" on-screen list (so
// students can be selected and notified) instead of a PDF.
export async function listPaymentsMade({ year, months, type, courseId, extraIds, taxIds, allMonthlyPaid, sucursalId }) {
  const id = sucursalId || getSucursalInfo()?.id || 1;
  const params = buildPaymentsMadeParams({ year, months, type, courseId, extraIds, taxIds, allMonthlyPaid });
  return API.get(`${baseURL}api/report/payments-made-multi-list/${id}`, { params });
}

// Ported from sge_infantil_web/src/pages/report/student/index.js `getReport`.
// sge_infantil_core's GET /api/report/payments/:sucursalId (routes/report.js)
// resolves the actual date window itself (utils/DatetimeUtils.js#getDateRange),
// preferring `day` over `year`+`month` over `startDate`+`endDate` — so callers
// only need to pass whichever of those three the user picked. Also the only
// endpoint that supports type=all ("Propinas + Extras", grouped by student),
// so RelatorioPagamentosLayer routes every filter combination through this
// one function instead of switching endpoints per filter.
export async function getPaymentReportPdfByDate({ day, startDate, endDate, year, month, type, sucursalId }) {
  const id = sucursalId || getSucursalInfo()?.id || 1;
  const params = { type };
  if (day) params.day = day;
  else if (startDate && endDate) {
    params.startDate = startDate;
    params.endDate = endDate;
  } else {
    params.year = year;
    params.month = month;
  }
  return API.get(`${baseURL}api/report/payments/${id}`, { params });
}

export async function getPaymentStats({ year, month, type, sucursalId }) {
  const id = sucursalId || getSucursalInfo()?.id || 1;
  return API.get(`${baseURL}api/payment/count/${id}/${year}/${month}`, { params: { type } });
}

// Ported from sge_infantil_web/src/pages/expenses/expenses/list/index.js
// `handleDownload` — same required year/month as `listExpenses` below.
// startDate/endDate are an alternative scope (Lista de Despesas' Data de
// Início/Fim range filter) — when given, the backend scopes the report to
// that range instead of year/month (routes/report.js GET /expenses/:id).
export async function getExpensesReportPdf({ year, month, expenseType, courseId, sucursalId, startDate, endDate }) {
  const id = sucursalId || getSucursalInfo()?.id || 1;
  return API.get(`${baseURL}api/report/expenses/${id}`, {
    params: { year, month, expenseType, corseId: courseId, startDate, endDate },
  });
}

// Ported from sge_infantil_web/src/pages/grade/pauta/index.js
// `handleDownload`/`handleDownload2` — the Pautas screen's Excel exports.
// Both endpoints require classId; respond with the uploaded file's URL as a
// plain string body (not JSON), so `resp.data` IS the URL.
//
// export-pauta-geral-xls: one subject+turma's gradebook, all evaluations.
export async function exportPautaGeralXls({ levelId, classId, subjectId, sucursalId }) {
  const id = sucursalId || getSucursalInfo()?.id || 1;
  return API.get(`${baseURL}api/report/export-pauta-geral-xls/${id}`, {
    params: { levelId, classId, subjectId },
  });
}

// export-gradebooks-xls: every subject's gradebook for a turma (optionally
// narrowed to one subject), all combined into one workbook.
export async function exportGradebooksXls({ levelId, classId, subjectId, sucursalId }) {
  const id = sucursalId || getSucursalInfo()?.id || 1;
  return API.get(`${baseURL}api/report/export-gradebooks-xls/${id}`, {
    params: { levelId, classId, subjectId },
  });
}
